import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_BASE } from '../config';
import '../styles/carrito.css';

function Carrito() {
  const { token, userRole, logout } = useAuth();
  const [poData, setPoData] = useState(null);
  const [cartDetails, setCartDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { ponumber } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [loadingStarted, setLoadingStarted] = useState(false);

  // Function to send the POST request to `/api/loading-start`
  const sendLoadingStart = async (poNumber) => {
    if (loadingStarted) return; // Prevent duplicate requests

    try {
      const response = await fetch(`${API_BASE}/loading-start/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${token}`,
        },
        body: JSON.stringify({ po_number: poNumber }),
      });

      if (!response.ok) {
        throw new Error(`Error sending loading-start request: ${response.statusText}`);
      }

      console.log(`Loading start registered for PO ${poNumber}`);
      setLoadingStarted(true);

    } catch (error) {
      console.error('Failed to send loading-start request:', error);
    }
  };

  useEffect(() => {
    const fetchPoData = async () => {
      setLoading(true);
      setError(null);

      try {
      // First try to use order data from navigation state
      if (location.state?.orderData) {
        setPoData(location.state.orderData);
        if (location.state.orderData.carts) {
          const details = location.state.orderData.carts.map(cart => ({
            code: cart.code,
            responsable: cart.code.startsWith('CART')
              ? location.state.orderData.item_responsable.map(r => r.name).join(', ') || 'No tiene responsable'
              : location.state.orderData.frozen_responsable.map(r => r.name).join(', ') || 'No tiene responsable',
          }));
          setCartDetails(details);
        }
          setLoading(false);
        return;
      }

      // If no order data in state, fetch from get-po endpoint
      if (!token) {
          throw new Error('No token available');
      }

        const response = await fetch(`${API_BASE}/get-po/?po_number=${ponumber}`, {
          method: 'GET',
          headers: {
            'Authorization': `Token ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch PO data: ${response.status} ${response.statusText}`);
        }

          const data = await response.json();
          setPoData(data);

          if (data.carts && Array.isArray(data.carts)) {
            const details = data.carts.map(cart => ({
              code: cart.code,
              responsable: cart.code.startsWith('CART')
                ? data.item_responsable.map(r => r.name).join(', ') || 'No tiene responsable'
                : data.frozen_responsable.map(r => r.name).join(', ') || 'No tiene responsable',
            }));
            setCartDetails(details);
          } else {
            console.log(`No carts found for PO number: ${ponumber}`);
          setCartDetails([]);
        }
      } catch (error) {
        console.error('Error:', error);
        setError(error.message);
        setPoData(null);
        setCartDetails([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPoData();
  }, [token, ponumber, location.state]);

  // Effect to handle loading-start after cart details are set
  useEffect(() => {
    if (cartDetails.length > 0 && !loadingStarted) {
      sendLoadingStart(ponumber);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartDetails, loadingStarted, ponumber]);

  const formatCartCode = (code) => {
    if (code.startsWith('CART-')) {
      return `Abarrote Nº${code.split('-')[1]}`;
    } else if (code.startsWith('BASKET-')) {
      return `Frío Nº${code.split('-')[1]}`;
    }
    return code;
  };

  const handleBack = () => {
    if (userRole === 'picker' || userRole === 'frozen') {
      navigate('/picking');
    } else if (userRole === 'admin') {
      navigate('/carga');
    }
  };

  const handleNext = () => {
    // Pass the order data to the next component
    navigate(`/paid/${ponumber}/productos`, { state: { orderData: poData } });
  };

  const handleLogout = () => {
    logout();
  };

  if (loading) {
    return (
      <div className="carrito-container">
        <div className="carrito-loading">
          <div className="loading-spinner"></div>
          <p>Cargando información del pedido...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="carrito-container">
        <button onClick={handleBack} className="carrito-back-button">
          &#8592;
        </button>
        <div className="carrito-error">
          <div className="error-icon">⚠️</div>
          <h2>Error al cargar el pedido</h2>
          <p>{error}</p>
          <button onClick={() => window.location.reload()} className="retry-button">
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="carrito-container">
      {/* Header */}
      <div className="carrito-header">
        <button onClick={handleBack} className="carrito-back-button">
        &#8592;
      </button>
        <button onClick={handleLogout} className="carrito-logout-button">
          Cerrar Sesión
        </button>
      </div>

      {/* Main Content */}
      <div className="carrito-content">
        <h1 className="carrito-title">Pedido {ponumber}</h1>
        <p className="carrito-subtitle">Carritos asignados para carga</p>

      {cartDetails.length > 0 ? (
          <div className="carrito-list">
            {cartDetails.map((cart, index) => (
              <div key={index} className="carrito-item">
                <div className="cart-icon">
                  {cart.code.startsWith('CART-') ? '🛒' : '❄️'}
                </div>
                <div className="cart-info">
                  <h3 className="cart-code">{formatCartCode(cart.code)}</h3>
                  <p className="cart-responsable">Responsable: {cart.responsable}</p>
                </div>
                <div className="cart-status">
                  <div className="status-indicator ready"></div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="carrito-no-items">
            <div className="no-items-icon">📦</div>
            <h3>No hay carritos asignados</h3>
            <p>Este pedido no tiene carritos asignados para carga.</p>
        </div>
      )}

        {userRole === 'admin' && cartDetails.length > 0 && (
          <button onClick={handleNext} className="carrito-next-button">
            Continuar con productos
        </button>
      )}
      </div>
    </div>
  );
}

export default Carrito;
